import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { MAGNIFLEX_LOGO_URL } from "@/lib/brand";
import { LogOut, BarChart3, Building2, Package, Ticket, ListOrdered, Mail, MessageSquare } from "lucide-react";
import HotelsManager from "@/components/admin/HotelsManager";
import ProductsManager from "@/components/admin/ProductsManager";
import OrdersManager from "@/components/admin/OrdersManager";
import AnalyticsDashboard from "@/components/admin/AnalyticsDashboard";
import EmailConfigPanel from "@/components/admin/EmailConfigPanel";
import ReviewsDashboard from "@/components/ReviewsDashboard";

const TABS = [
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "reviews", label: "Recensioni", icon: MessageSquare },
  { key: "hotels", label: "Hotel", icon: Building2 },
  { key: "products", label: "Prodotti", icon: Package },
  { key: "orders", label: "Ordini & Match", icon: ListOrdered },
  { key: "email", label: "Email SMTP", icon: Mail },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("analytics");

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F5F6F7]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#E5E7EB] shadow-sm">
        <div className="absolute top-0 left-0 w-full h-1 bg-[#E30613]" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={MAGNIFLEX_LOGO_URL} alt="Magniflex" className="h-9" />
            <div className="hidden md:block border-l border-[#E5E7EB] pl-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#6B7280] font-semibold">Hospitality CMS</div>
              <div className="mgx-display text-sm text-[#003D5C] mt-0.5">Back Office</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#6B7280] hidden md:block" data-testid="admin-user-email">{user?.email}</span>
            <button
              onClick={handleLogout}
              className="mgx-btn-secondary text-xs gap-2"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <nav className="flex overflow-x-auto -mb-px" data-testid="admin-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-2 px-5 py-4 text-xs uppercase tracking-[0.2em] font-semibold border-b-2 whitespace-nowrap transition-colors ${
                    active
                      ? "border-[#E30613] text-[#003D5C]"
                      : "border-transparent text-[#6B7280] hover:text-[#003D5C]"
                  }`}
                  data-testid={`admin-tab-${t.key}`}
                >
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        {tab === "analytics" && <AnalyticsDashboard />}
        {tab === "reviews" && <ReviewsDashboard apiEndpoint="/admin/reviews-dashboard" scope="admin" />}
        {tab === "hotels" && <HotelsManager />}
        {tab === "products" && <ProductsManager />}
        {tab === "orders" && <OrdersManager />}
        {tab === "email" && <EmailConfigPanel />}
      </main>
    </div>
  );
}
